import {create} from 'zustand';
import {
    FloorplanReservationItemDTO
} from "crqlar-react-common/models/floorplan/floorplanReservationItem.dto";
import {
    FloorplanListHalfboardDTO
} from "crqlar-react-common/models/floorplan/floorplanListHalfboard.dto";
import {FloorplanTable} from "../types";

interface Store {
    assignedReservations: FloorplanReservationItemDTO[];
    unassignedReservations: FloorplanReservationItemDTO[];
    halfboardProfiles: FloorplanListHalfboardDTO[];

    floorplanTables: FloorplanTable[]

    highlightedTableIds: Set<string>;

    locateRequest: FloorplanLocateRequest | null;

    sidebarTab: 0 | 1 | 2;
}

interface Actions {
    patch: (partial: Partial<Store>) => void;
    highlightTable: (tableIds: string | string[]) => void;
    requestLocateReservation: (reservation: FloorplanReservationItemDTO) => void;
    consumeLocateRequest: () => FloorplanLocateRequest | null;
}

export interface FloorplanLocateRequest {
    tableIds: string[];
    start?: Date;
    end?: Date;
}

const toValidDate = (value: unknown): Date | null => {
    if (value == null) return null;

    const date = value instanceof Date ? new Date(value) : new Date(value as string | number);
    return Number.isNaN(date.getTime()) ? null : date;
};

const defaultStore: Store = {
    assignedReservations: [],
    unassignedReservations: [],
    halfboardProfiles: [],

    floorplanTables: [],

    highlightedTableIds: new Set(),

    locateRequest: null,

    sidebarTab: (
        typeof localStorage === "undefined"
            ? 0
            : Number(localStorage.getItem("floorplanSidebarTab")) || 0
    ) as 0 | 1 | 2,
}

export const useReadStore = create<Store & Actions>((set, get) => ({
    ...defaultStore,
    patch: (partial: Partial<Store>) => set((state) => ({...state, ...partial})),
    highlightTable: (tableIds: string | string[]) => {
        const ids = Array.isArray(tableIds) ? tableIds : [tableIds];
        set((state) => {
            const next = new Set(state.highlightedTableIds);
            ids.forEach(id => next.add(id));
            return {highlightedTableIds: next};
        });
        setTimeout(() => {
            set((state) => {
                const next = new Set(state.highlightedTableIds);
                ids.forEach(id => next.delete(id));
                return {highlightedTableIds: next};
            });
        }, 2500);
    },
    requestLocateReservation: (reservation: FloorplanReservationItemDTO) => {
        const tableIds = Array.from(new Set(
            (reservation.tableResources ?? [])
                .map(table => table.id)
                .filter((id): id is string => Boolean(id)),
        ));
        const start = toValidDate(reservation.restaurantReservation?.expectedStartDate);
        const end = toValidDate(reservation.restaurantReservation?.expectedEndDate);

        if (!tableIds.length || !start || !end) return;

        set({locateRequest: {tableIds, start, end}});
    },
    consumeLocateRequest: () => {
        const request = get().locateRequest;
        if (request) set({locateRequest: null});
        return request;
    },
}));
